// Staff spell editor view - create/edit spells with Monaco JSON editor

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import '../shared/monaco-json-editor.js';

// Default spell template for new spells
const defaultSpellJson = {
  slug: 'new-spell',
  name: 'New Spell',
  description: 'A description of this spell.',
  level: 0,
  school: 'evocation',
  classLists: ['wizard'],
  casting: {
    time: 'action',
    components: {
      verbal: true,
      somatic: true,
    },
  },
  range: {
    type: 'distance',
    distance: 60,
  },
  duration: {
    type: 'instantaneous',
  },
};

/**
 * Staff spell editor view component for creating/editing spells using Monaco
 */
class StaffSpellEditorView extends BaseComponent {
  static properties = {
    slug: { type: String },
    _loading: { type: Boolean, state: true },
    _saving: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _validationErrors: { type: Array, state: true },
    _published: { type: Boolean, state: true },
    _version: { type: Number, state: true },
    _spellName: { type: String, state: true },
    _spellJson: { type: String, state: true },
    _hasEditorErrors: { type: Boolean, state: true },
    _versions: { type: Array, state: true },
  };

  static styles = css`
    :host {
      position: absolute;
      inset: 0;
      overflow: hidden;
    }
  `;

  constructor() {
    super();
    /** @type {string|null} */
    this.slug = null;
    this._loading = true;
    this._saving = false;
    this._error = null;
    /** @type {Array<string>} */
    this._validationErrors = [];
    this._published = false;
    this._version = 1;
    this._spellName = '';
    this._spellJson = JSON.stringify(defaultSpellJson, null, 2);
    this._hasEditorErrors = false;
    /** @type {Array<{version: number, published: boolean}>} */
    this._versions = [];
  }

  get _isEditMode() {
    return !!this.slug;
  }

  get _isValidJson() {
    try {
      const parsed = JSON.parse(this._spellJson);
      return parsed?.slug && parsed.name;
    } catch {
      return false;
    }
  }

  get _isLatestVersion() {
    if (!this._versions.length) return true;
    return this._version === this._versions[0].version;
  }

  get _canSave() {
    return this._isValidJson && !this._hasEditorErrors && this._isLatestVersion;
  }

  async connectedCallback() {
    await super.connectedCallback();
    if (this._isEditMode) {
      await this._loadSpell();
    } else {
      this._loading = false;
    }
  }

  async _loadSpell() {
    this._loading = true;
    this._error = null;

    try {
      const data = await api.get(`/api/staff/spells/${this.slug}`);
      this._spellJson = JSON.stringify(data.spellJson, null, 2);
      this._published = data.published;
      this._version = data.version || 1;
      this._spellName = data.name;

      // Load versions list
      try {
        this._versions = await api.get(`/api/staff/spells/${this.slug}/versions`);
      } catch {
        this._versions = [];
      }
    } catch (err) {
      console.error('Failed to load spell:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load spell';
    } finally {
      this._loading = false;
    }
  }

  async _handleVersionChange(e) {
    const version = parseInt(/** @type {HTMLSelectElement} */ (e.target).value, 10);
    try {
      const data = await api.get(`/api/staff/spells/${this.slug}?version=${version}`);
      this._spellJson = JSON.stringify(data.spellJson, null, 2);
      this._published = data.published;
      this._version = data.version || 1;
      this._spellName = data.name;
    } catch (err) {
      this._error = /** @type {Error} */ (err).message || 'Failed to load version';
    }
  }

  /** @param {Event} e */
  _handlePublishedChange(e) {
    this._published = /** @type {HTMLInputElement} */ (e.target).checked;
  }

  _dismissError() {
    this._error = null;
  }

  _goBack() {
    router.navigate('/staff/spells');
  }

  async _saveSpell() {
    this._saving = true;
    this._error = null;
    this._validationErrors = [];

    try {
      let parsedJson;
      try {
        parsedJson = JSON.parse(this._spellJson);
      } catch (parseErr) {
        this._error = `Invalid JSON: ${parseErr.message}`;
        return;
      }

      const payload = {
        spellJson: parsedJson,
        published: this._published,
      };

      if (this._isEditMode) {
        await api.put(`/api/staff/spells/${this.slug}`, payload);
      } else {
        await api.post('/api/staff/spells', payload);
      }

      router.navigate('/staff/spells');
    } catch (err) {
      console.error('Failed to save spell:', err);
      const errorMessage = /** @type {Error} */ (err).message;
      if (errorMessage?.includes('validation failed')) {
        const errorLines = errorMessage.split('\n');
        if (errorLines.length > 1) {
          this._validationErrors = errorLines.slice(1).filter((/** @type {string} */ line) => line.trim());
        }
      }
      if (this._validationErrors.length === 0) {
        this._error = errorMessage || 'Failed to save spell';
      }
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      <div class="container-fluid p-4 d-flex flex-column" style="height: 100%;">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">Staff</li>
            <li class="breadcrumb-item">
              <a
                href="#"
                @click="${(/** @type {Event} */ e) => {
                  e.preventDefault();
                  this._goBack();
                }}"
                >Spells</a
              >
            </li>
            <li class="breadcrumb-item active" aria-current="page">${this._isEditMode ? 'Edit' : 'New'}</li>
          </ol>
        </nav>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center gap-2">
            <h2 class="mb-0">${this._isEditMode ? `Edit Spell: ${this._spellName}` : 'New Spell'}</h2>
            ${
              this._isEditMode
                ? html`
                  ${
                    this._versions.length > 1
                      ? html`
                        <select
                          class="form-select form-select-sm d-inline-block"
                          style="width: auto;"
                          .value="${String(this._version)}"
                          @change="${this._handleVersionChange}"
                        >
                          ${this._versions.map(
                            (v) => html`
                              <option value="${v.version}" ?selected="${v.version === this._version}">
                                v${v.version} ${v.published ? '(published)' : '(draft)'}
                              </option>
                            `,
                          )}
                        </select>
                      `
                      : html`<span class="badge bg-secondary">v${this._version}</span>`
                  }
                  <span class="badge ${this._published ? 'bg-success' : 'bg-warning text-dark'}">
                    ${this._published ? 'Published' : 'Draft'}
                  </span>
                `
                : ''
            }
          </div>
          <div class="d-flex gap-2 align-items-center">
            ${
              this._isLatestVersion
                ? html`
                  <div class="form-check mb-0">
                    <input
                      id="publishedCheck"
                      class="form-check-input"
                      type="checkbox"
                      .checked="${this._published}"
                      @change="${this._handlePublishedChange}"
                    />
                    <label class="form-check-label" for="publishedCheck">Published</label>
                  </div>
                `
                : ''
            }
            <button type="button" class="btn btn-outline-secondary" @click="${this._goBack}">Cancel</button>
            ${
              this._isLatestVersion
                ? html`
                  <button
                    type="button"
                    class="btn btn-primary"
                    ?disabled="${this._saving || !this._canSave}"
                    @click="${this._saveSpell}"
                  >
                    ${
                      this._saving ? html`<span class="spinner-border spinner-border-sm me-1"></span>Saving...` : 'Save'
                    }
                  </button>
                `
                : ''
            }
          </div>
        </div>
        ${
          this._isEditMode && this._isLatestVersion && this._published
            ? html`<div class="alert alert-info py-2 mb-3">
              This version is published. Saving will create a new version.
            </div>`
            : ''
        }
        ${
          this._isEditMode && !this._isLatestVersion
            ? html`<div class="alert alert-secondary py-2 mb-3">
              Viewing historical version. Switch to the latest version to edit.
            </div>`
            : ''
        }

        ${
          this._error
            ? html`
              <div class="alert alert-danger alert-dismissible fade show">
                ${this._error}
                <button type="button" class="btn-close" @click="${this._dismissError}"></button>
              </div>
            `
            : ''
        }
        ${
          this._validationErrors.length > 0
            ? html`
              <div class="alert alert-warning">
                <strong>Validation Errors:</strong>
                <ul class="mb-0 mt-1">
                  ${this._validationErrors.map((err) => html`<li>${err}</li>`)}
                </ul>
              </div>
            `
            : ''
        }

        <monaco-json-editor
          class="flex-grow-1"
          .value="${this._spellJson}"
          .loading="${this._loading}"
          schema-url="/api/schemas/spell-schema.json"
          ref-schema-url="/api/schemas/core-schema.json"
          @value-changed="${(/** @type {CustomEvent} */ e) => {
            this._spellJson = e.detail.value;
          }}"
          @errors-changed="${(/** @type {CustomEvent} */ e) => {
            this._hasEditorErrors = e.detail.hasErrors;
          }}"
        ></monaco-json-editor>
      </div>
    `;
  }
}

customElements.define('staff-spell-editor-view', StaffSpellEditorView);
